import { useCallback, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconNote } from '@tabler/icons-react';
import { CrudMode } from '@ohpen/enso-ui';
import { useAuth } from '@ohpen/react-auth';

import { NoteResponse } from '@pmp/api';
import { usePmpFetch } from '@pmp/hooks';
import { useValueListsStore } from '@pmp/stores';
import { getNotesInitialState, NotesCrudForm, NotesList, notesReducer, NotesDeleteModal, NOTES_LIST_NAMES } from '@pmp/components';

import { TabProps } from '../interfaces';
import { TabContent } from './TabContent';
import { CrudFormCard } from './CrudFormCard';
import styles from '../styles.module.css';
import { Flex } from 'antd';

export const NotesTab: React.FC<TabProps> = ({ form, entityType }) => {
  const [, , data, , fetchData] = usePmpFetch('notesGetNotes');
  const { valueLists, getValueLists } = useValueListsStore();
  const [state, dispatch] = useReducer(notesReducer<NoteResponse>, getNotesInitialState<NoteResponse>());
  const { t } = useTranslation();
  const { userName } = useAuth();

  const entityId = form?.getFieldValue?.('firmId') as string;

  useEffect(() => {
    getValueLists({ alwaysFetch: NOTES_LIST_NAMES });
  }, [getValueLists]);

  const refreshTable = useCallback(() => {
    fetchData({ query: { entityType, entityId } });
  }, [fetchData, entityType, entityId]);

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onEdit = useCallback((record: NoteResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: NoteResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <TabContent noScroll>
      <Flex className={styles.notesContainer}>
        <CrudFormCard icon={<IconNote />} title={t('global.labels.notes')}>
          <NotesList
            notes={data ?? []}
            valueLists={valueLists}
            onCreate={onCreate}
            onEdit={onEdit}
            onDelete={onDelete}
            userName={userName}
          />
        </CrudFormCard>
      </Flex>
      {state.crud.open && state.crud.mode && (
        <NotesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={refreshTable}
          onClose={onCloseCrud}
          entityType={entityType}
          entityId={entityId}
          userName={userName}
        />
      )}

      {state.deleteModal.open && (
        <NotesDeleteModal
          open={state.deleteModal.open}
          record={state.deleteModal.record}
          onClose={onCloseDeleteModal}
          refreshTable={refreshTable}
        />
      )}
    </TabContent>
  );
};
